import * as React from "react";
import {
  Theme,
  makeStyles,
  createStyles,
  Popper,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Card,
  ClickAwayListener,
  Button,
  CircularProgress,
} from "@material-ui/core";
import NotificationsActiveIcon from "@material-ui/icons/NotificationsActive";
import NotificationOff from "@material-ui/icons/NotificationsNone";
import clsx from "clsx";
import NotificationDetailModel from "../../core/models/NotificationDetail";
import { format } from "date-fns";
import { calculateLevelPercentage } from "../../helpers/installationTankHelpers";
import { isArrayValid } from "../../helpers/utility";
import { APP_DEFAULT_NUMBER_OF_NOTIFICATIONS } from "../../core/constants/appGlobals";
import { Skeleton } from "@material-ui/lab";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    buttonRoot: {
      "height": "2em",
      "width": "2em",
      "background": theme.palette.grey[300],
      "position": "relative",

      "&.clear-notification": {
        background: "inherit",
      },

      [theme.breakpoints.down("xs")]: {
        height: "1.5em",
        width: "1.5em",
      },
    },

    titleNotific: {
      textAlign: "center",
      fontSize: "0.7rem",
      fontWeight: "bolder",
      color: theme.palette.grey[600],
    },
    root: {
      "width": "300px",
      "& .MuiAccordion-root.Mui-expanded": {
        margin: 0,
      },
    },

    installationGroup: {
      fontSize: "0.6rem",
      maxWidth: "55%",
      flex: 0.65,
    },

    notificationUnread: {
      textAlign: "center",
      fontSize: "0.7rem",
      color: theme.palette.grey[500],
    },

    createdDate: {
      fontSize: "0.6rem",
      flex: 1,
      textAlign: "end",
    },

    flexBoxRow: {
      display: "table-row",
      justifyContent: "space-between",
      alignItems: "center",
    },

    icon: {
      "& .MuiSvgIcon-root": {
        fontSize: "2.5rem",
        [theme.breakpoints.down("sm")]: {
          fontSize: "2rem",
        },
      },
    },

    opaque: {
      color: "transparent",
    },

    titleRuleRed: {
      color: theme.palette.error.main,
      fontWeight: "bolder",
    },

    titleRuleGreen: {
      color: theme.palette.primary.main,
      fontWeight: "bolder",
    },

    titleRuleBlack: {
      color: theme.palette.common.black,
      fontWeight: "bolder",
    },

    titleNotificDetail: {
      "textAlign": "center",
      "fontSize": "0.8rem",
      "fontWeight": "bolder",
      "display": "flex",
      "alignItems": "center",

      "& > *:first-child": {
        flex: 1,
        color: theme.customPalette.text.primary,
      },
    },

    listRoot: {
      "paddingBottom": "unset",
      "paddingTop": "unset",

      "& .MuiListItem-gutters": {
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
      },

      "& > *": {
        "justifyContent": "space-between",
        "padding": theme.spacing(2),

        "&.center-adjust": {
          justifyContent: "center",
        },
        "&:nth-child(even):not(.center-adjust)": {
          background: theme.customPaper.background.cardBackgroundLight,
        },
        "&:nth-child(odd):not(.center-adjust)": {
          background: theme.palette.common.white,
        },
      },
    },

    notificationsWrapper: {
      "padding": theme.spacing(2, 0),
      "display": "flex",
      "flexDirection": "column",
      "minWidth": 300,

      "& ul": {
        "overflow": "auto",
        "maxHeight": "80vh",

        [theme.breakpoints.only("sm")]: {
          maxHeight: "60vh",
        },

        [theme.breakpoints.down("xs")]: {
          minWidth: "100vw",
          minHeight: `calc(100vh - 10px - ${theme.mixins.toolbar.minHeight}px)`,
        },

        "& > *": {
          display: "flex",
          flex: 1,
          width: "100%",
        },
      },
    },

    clearButton: {
      color: theme.customPalette.text.link,
      fontSize: "0.5rem",
      width: "min-content",
    },

    showMoreButton: {
      width: "100%",
      color: theme.customPalette.text.link,
    },

    messageWrapper: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    },

    numberOfAlerts: {
      position: "absolute",
      left: "-0.7rem",
      bottom: 2,
      background: theme.palette.error.main,
      borderRadius: "45%",
      width: "2em",
      fontSize: "0.9rem",
      color: theme.palette.common.white,
    },

    popper: {
      "zIndex": 1499,
      '&[x-placement*="bottom"] $arrow': {
        "top": 0,
        "left": 0,
        "marginTop": "-0.9em",
        "width": "3em",
        "height": "1em",
        "&::before": {
          borderWidth: "0 1em 1em 1em",
          borderColor: `transparent transparent ${theme.palette.background.paper} transparent`,
        },
      },
      '&[x-placement*="top"] $arrow': {
        "bottom": 0,
        "left": 0,
        "marginBottom": "-0.9em",
        "width": "3em",
        "height": "1em",
        "&::before": {
          borderWidth: "1em 1em 0 1em",
          borderColor: `${theme.palette.background.paper} transparent transparent transparent`,
        },
      },
      '&[x-placement*="right"] $arrow': {
        "left": 0,
        "marginLeft": "-0.9em",
        "height": "3em",
        "width": "1em",
        "&::before": {
          borderWidth: "1em 1em 1em 0",
          borderColor: `transparent ${theme.palette.background.paper} transparent transparent`,
        },
      },
      '&[x-placement*="left"] $arrow': {
        "right": 0,
        "marginRight": "-0.9em",
        "height": "3em",
        "width": "1em",
        "&::before": {
          borderWidth: "1em 0 1em 1em",
          borderColor: `transparent transparent transparent ${theme.palette.background.paper}`,
        },
      },
    },
    arrow: {
      "position": "absolute",
      "fontSize": 7,
      "width": "3em",
      "height": "3em",
      "&::before": {
        content: '""',
        margin: "auto",
        display: "block",
        width: 0,
        height: 0,
        borderStyle: "solid",
      },
    },
  })
);

type NotificationComponentProps = {
  notifications?: NotificationDetailModel[];
  getGroupInstallation: (idInstallation: string) => string | undefined;
  getInstallation: (idInstallation: string, field: string) => string | number;
  handleClickNotification: (idNotification?: number, clearAll?: boolean) => void;
  handleClickShowMore: () => void;
  loading?: boolean;
  shouldShowRenderMore?: boolean;
  totalNotifications?: number;
};

function NotificationComponent({
  notifications,
  handleClickNotification,
  getInstallation,
  getGroupInstallation,
  handleClickShowMore,
  shouldShowRenderMore,
  totalNotifications,
  loading,
}: NotificationComponentProps) {
  const classes = useStyles();

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [open, setOpen] = React.useState<boolean>(false);
  const [arrowRef, setArrowRef] = React.useState<HTMLElement | null>(null);

  const handleToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
    setOpen((prev) => (event?.currentTarget !== anchorEl ? false : !prev));
    setAnchorEl(event?.currentTarget);
  };

  const existsNotifications = isArrayValid(notifications);

  return (
    <>
      <IconButton
        onClick={handleToggle}
        aria-label="Notificação"
        aria-haspopup="true"
        className={clsx(classes.buttonRoot, "avatar", !existsNotifications && "clear-notification")}
      >
        {existsNotifications ? (
          <>
            <NotificationsActiveIcon className="notification-icon" />
            <span className={classes.numberOfAlerts}>{totalNotifications}</span>
          </>
        ) : (
          <NotificationOff />
        )}
      </IconButton>

      <Popper
        id="menu-appbar"
        anchorEl={anchorEl}
        open={open}
        placement={"bottom-end"}
        className={classes.popper}
        disablePortal={false}
        modifiers={{
          flip: {
            enabled: false,
          },
          preventOverflow: {
            enabled: true,
            boundariesElement: "scrollParent",
          },
          arrow: {
            enabled: true,
            element: arrowRef,
          },
        }}
      >
        {open && (
          <>
            <span className={classes.arrow} ref={setArrowRef} />
            <ClickAwayListener onClickAway={() => setOpen(false)}>
              <Card className={classes.notificationsWrapper} elevation={3}>
                <div className={classes.titleNotificDetail}>
                  <Typography>Notificações</Typography>
                  <Button
                    className={classes.clearButton}
                    onClick={() => handleClickNotification(undefined, true)}
                    variant="text"
                    color="secondary"
                  >
                    Limpar tudo
                  </Button>
                </div>
                <List className={classes.listRoot}>
                  {loading &&
                    !existsNotifications &&
                    new Array(APP_DEFAULT_NUMBER_OF_NOTIFICATIONS)
                      .fill({})
                      .map((_, idx) => <Skeleton key={`notification-skeleton-${idx}`} />)}

                  {existsNotifications &&
                    notifications?.map((notification, idx) => (
                      <Tooltip key={idx} title={notification.message}>
                        <ListItem
                          className={classes.flexBoxRow}
                          key={idx}
                          button
                          onClick={() => handleClickNotification(notification.id)}
                        >
                          <ListItemText
                            primary={
                              <>
                                <span className={classes.titleRuleBlack}>
                                  {getInstallation(notification.installationId, "name")} #
                                  {notification.installationId}
                                  {` - `}
                                </span>
                                <span
                                  className={clsx(
                                    notification?.enumNotificationRule === 1
                                      ? classes.titleRuleRed
                                      : notification?.enumNotificationRule === 2
                                      ? classes.titleRuleGreen
                                      : classes.titleRuleBlack
                                  )}
                                >
                                  {`${notification.title}${
                                    notification?.enumNotificationRule === 1
                                      ? ` (${calculateLevelPercentage(
                                          getInstallation(
                                            notification.installationId,
                                            "level"
                                          ) as number,
                                          getInstallation(
                                            notification.installationId,
                                            "capacity"
                                          ) as number
                                        )}%)`
                                      : ""
                                  }`}
                                </span>
                              </>
                            }
                            secondary={
                              <div className={classes.messageWrapper}>
                                <span className={classes.installationGroup}>
                                  {getGroupInstallation(notification.installationId)}
                                </span>
                                <span className={classes.createdDate}>
                                  {format(new Date(notification.createDate), "dd/MM/yyyy HH:mm:ss")}
                                </span>
                              </div>
                            }
                          ></ListItemText>
                        </ListItem>
                      </Tooltip>
                    ))}
                  {shouldShowRenderMore && !loading && (
                    <ListItem>
                      <ListItemText>
                        <Button
                          className={classes.showMoreButton}
                          onClick={handleClickShowMore}
                          variant="text"
                        >
                          Ver mais...
                        </Button>
                      </ListItemText>
                    </ListItem>
                  )}

                  {existsNotifications && loading && (
                    <ListItem className={"center-adjust"}>
                      <CircularProgress />
                    </ListItem>
                  )}

                  {!existsNotifications && (
                    <Typography className={classes.notificationUnread}>
                      Todas as notificações já foram lidas
                    </Typography>
                  )}
                </List>
              </Card>
            </ClickAwayListener>
          </>
        )}
      </Popper>
    </>
  );
}

export default NotificationComponent;
